import { useTranslation } from 'react-i18next';

import { formatDisplayDate } from '@/utils/date-formatters';

import { getTimeOffTypeIcon } from '../../constants/time-off-type-icon-map';
import { TimeOffBalance } from '../../types/time-off-balance';

interface TimeOffTypesListCardProps {
  balances: TimeOffBalance[];
}

/**
 * Right-column card shown in create mode before a category is picked: lists every
 * time-off type with its icon + available balance, so the user can see what they
 * have before choosing. Styled like the balance card (muted surface).
 */
export const TimeOffTypesListCard = ({ balances }: TimeOffTypesListCardProps) => {
  const { t } = useTranslation('authenticated.timeOff');

  const formatDaysValue = (value: number) => t('balances.daysValue', { count: value });

  return (
    <div className="flex flex-col gap-4 rounded-xl border bg-muted/40 p-5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-base font-semibold">{t('details.balance.title')}</span>
        <span className="text-xs text-muted-foreground">
          {t('details.balance.today', { date: formatDisplayDate(new Date()) })}
        </span>
      </div>

      <div className="flex flex-col gap-4">
        {balances.map(balance => {
          const Icon = getTimeOffTypeIcon(balance.type.code);
          return (
            <div key={balance.type.id} className="flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-foreground text-background">
                <Icon className="h-4 w-4" />
              </div>
              <div className="flex min-w-0 flex-col">
                <span className="truncate text-sm font-semibold">{balance.type.displayName}</span>
                <span className="text-xs text-muted-foreground">
                  {t('details.balance.daysAvailable', {
                    value: formatDaysValue(balance.availableDays),
                  })}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
